import {
  forwardRef,
  type ButtonHTMLAttributes,
  type PropsWithChildren,
} from "react";
import clsx from "clsx";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "outline" | "destructive";
  /** Optional size variant for compact buttons used across the UI */
  size?: "sm" | "md";
}

export const Button = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<ButtonProps>
>(({ variant = "primary", className, children, size = "md", ...props }, ref) => {
  const base =
    "inline-flex items-center justify-center rounded-action text-body font-medium transition-colors duration-fast ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-primary disabled:cursor-not-allowed disabled:opacity-60";

  const sizeClasses: Record<NonNullable<ButtonProps["size"]>, string> = {
    md: "min-h-control-md px-6 py-2.5",
    sm: "min-h-control-sm px-3 py-1.5 text-xs",
  };

  const sizeClass = sizeClasses[size];
  const variantClasses: Record<NonNullable<ButtonProps["variant"]>, string> = {
    primary: "bg-primary text-primary-foreground hover:brightness-95",
    secondary:
      "border border-border-strong bg-surface-primary text-content-secondary hover:bg-surface-secondary",
    ghost:
      "bg-transparent text-content-secondary hover:bg-surface-secondary",
    outline:
      "border border-border-strong bg-surface-primary text-content-secondary hover:bg-surface-secondary",
    destructive:
      "bg-state-danger text-content-inverse hover:brightness-95",
  };

  return (
    <button
      ref={ref}
      className={clsx(base, sizeClass, variantClasses[variant], className)}
      {...props}
    >
      {children}
    </button>
  );
});

Button.displayName = "Button";
